<template>
  <div class="fu-pop-root">
    <div class="fu-pop__scrim" :class="{ 'fu-pop__scrim--tint': isPhone }" data-test="fu-popover-scrim" @click="$emit('close')"></div>
    <div
      ref="panel"
      class="fu-pop"
      :class="{ 'fu-pop--phone': isPhone }"
      :style="panelStyle"
      role="dialog"
      :aria-label="title"
      data-test="fu-popover"
      :data-state="fu.state"
    >
      <div class="fu-pop__head">
        <div class="fu-pop__eyebrow">{{ eyebrow }}</div>
        <div class="fu-pop__title">{{ title }}</div>
        <div v-if="sub" class="fu-pop__sub">{{ sub }}</div>
      </div>

      <div v-if="canSchedule" class="fu-pop__section" data-test="fu-popover-schedule">
        <label class="fu-pop__label" :for="dateId">{{ dateLabel }}</label>
        <div class="fu-pop__presets" role="group" :aria-label="`${dateLabel} presets`">
          <button
            v-for="p in presets"
            :key="p.label"
            type="button"
            class="fu-pop__preset"
            :class="{ 'fu-pop__preset--on': preset === p.label }"
            :aria-pressed="preset === p.label ? 'true' : 'false'"
            data-test="fu-popover-preset"
            @click="pickPreset(p)"
          >{{ p.label }}</button>
        </div>
        <input
          :id="dateId"
          ref="date"
          type="date"
          class="fu-pop__input"
          :min="today"
          :value="date"
          :aria-invalid="dateError ? 'true' : 'false'"
          :aria-describedby="dateError ? `${dateId}-err` : null"
          data-test="fu-popover-date"
          @input="onDateInput"
          @change="onDateInput"
        />
        <div v-if="dateError" :id="`${dateId}-err`" class="fu-pop__error" data-test="fu-popover-date-error">{{ dateError }}</div>

        <template v-if="showAssign">
          <label class="fu-pop__label fu-pop__label--gap" :for="`${dateId}-as`">Assign to</label>
          <select
            :id="`${dateId}-as`"
            v-model="employeeId"
            class="fu-pop__input fu-pop__select"
            data-test="fu-popover-assign"
          >
            <option value="">Select employee (optional)</option>
            <option v-for="e in employees" :key="e.EmployeeID" :value="String(e.EmployeeID)">{{ e.Name }}</option>
          </select>
        </template>

        <template v-if="isInitial">
          <label class="fu-pop__label fu-pop__label--gap" :for="`${dateId}-note`">
            Note <span class="fu-pop__optional">· optional</span>
          </label>
          <textarea
            :id="`${dateId}-note`"
            v-model="note"
            rows="3"
            class="fu-pop__input fu-pop__textarea"
            :placeholder="notePlaceholder"
            data-test="fu-popover-note"
          ></textarea>
        </template>

        <div class="fu-pop__actions">
          <button type="button" class="fu-pop__btn fu-pop__btn--ghost" @click="$emit('close')">Cancel</button>
          <button
            type="button"
            class="fu-pop__btn fu-pop__btn--primary"
            :disabled="!canSave"
            data-test="fu-popover-save"
            @click="save"
          >{{ isInitial ? 'Start follow-ups' : 'Save' }}</button>
        </div>
      </div>

      <div v-if="hasDanger" class="fu-pop__danger" data-test="fu-popover-danger" :data-action="fu.dangerAction">
        <div v-if="confirming !== 'danger'" class="fu-pop__danger-row">
          <div class="fu-pop__danger-text">
            <div class="fu-pop__danger-title">{{ danger.title }}</div>
            <div class="fu-pop__danger-desc">{{ danger.desc }}</div>
          </div>
          <button type="button" class="fu-pop__danger-cta" data-test="fu-popover-danger-cta" @click="confirming = 'danger'">
            {{ danger.cta }}
          </button>
        </div>
        <template v-else>
          <div class="fu-pop__confirm-text" data-test="fu-popover-confirm-text">{{ danger.confirmText }}</div>
          <div class="fu-pop__actions">
            <button type="button" class="fu-pop__btn fu-pop__btn--ghost" @click="confirming = null">Keep</button>
            <button type="button" class="fu-pop__btn fu-pop__btn--danger" data-test="fu-popover-confirm" @click="confirmDanger">
              {{ danger.confirmCta }}
            </button>
          </div>
        </template>
      </div>

      <div v-if="fu.canRestart" class="fu-pop__section" data-test="fu-popover-restart">
        <div class="fu-pop__info">
          <fu-icon name="restart" :size="15" class="fu-pop__info-icon" />
          <div>Restarting resets attempts to 0 and sets the status back to <strong>Not Called</strong>, so you can schedule a new first follow-up.</div>
        </div>
        <div v-if="confirming !== 'restart'" class="fu-pop__actions">
          <button v-if="!canSchedule" type="button" class="fu-pop__btn fu-pop__btn--ghost" @click="$emit('close')">Cancel</button>
          <button type="button" class="fu-pop__btn fu-pop__btn--secondary" data-test="fu-popover-restart-cta" @click="confirming = 'restart'">
            <fu-icon name="restart" :size="15" />
            Restart follow-ups
          </button>
        </div>
        <template v-else>
          <div class="fu-pop__confirm-text fu-pop__confirm-text--plain" data-test="fu-popover-confirm-text">Start the follow-up cycle over?</div>
          <div class="fu-pop__actions">
            <button type="button" class="fu-pop__btn fu-pop__btn--ghost" @click="confirming = null">Keep</button>
            <button type="button" class="fu-pop__btn fu-pop__btn--primary" data-test="fu-popover-confirm" @click="confirmRestart">Yes, restart</button>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * The follow-up popover — what clicking a Follow-up cell opens (the export's lines 550–621 /
 * fuPopVM), mapped onto TODAY's rules (resolveFollowupState):
 *
 *   schedule section  when the user can edit and the state has a date to set or move:
 *                     notset → "Start following up" (first date + note + assignee) — the legacy
 *                     "Not Set" dropdown, tasks/updateInitialFollowupDate;
 *                     due / past / upcoming / nodate → "Move follow-up to" (+ assignee) — the legacy
 *                     date dropdown, tasks/updateFollowupDate.
 *   danger section    fu.dangerAction: 'remove-initial' (untouched auto-created first follow-up) or
 *                     'stop' — exactly one, as legacy shows exactly one red button.
 *   restart section   fu.canRestart (stopped / done; ignores edit permission, as legacy's icon does).
 *
 * Stop / remove / restart are confirmed INLINE ("Keep" / confirm) — this replaces the three legacy
 * "Are you sure?" b-modals. Dates are a native <input type="date">, not before today (legacy
 * notBefore); a typed earlier or unreadable date shows an error and disables Save. The note
 * placeholder is the legacy rule (GENERAL_FOLLOWUP_NOTE_COPY_TO_COMMLOG), passed in.
 *
 * Escape or a click outside closes it. Desktop / tablet: fixed beside the cell, flipped above it
 * when there is no room below, clamped inside the viewport. Phone: a bottom sheet with its action
 * buttons on the LEFT — the app's floating agent button (z 9999) sits over the bottom-right corner.
 *
 * Emits: save({ kind: 'initial'|'change', date: 'YYYY-MM-DD', note, employeeId }), remove-initial,
 * stop, restart, close.
 */
import FuIcon from './FuIcon.vue';
import { formatDate, daysBetween } from '@/utils/followups/rowModel.js';

const WIDTH = 344;
const EST_HEIGHT = 420;
const EDGE = 12;
const YMD = /^\d{4}-\d{2}-\d{2}$/;

/** 'YYYY-MM-DD' + n days, by calendar (no time zone involved). */
function addDays(ymd, n) {
  const d = new Date(Date.UTC(Number(ymd.slice(0, 4)), Number(ymd.slice(5, 7)) - 1, Number(ymd.slice(8, 10)) + n));
  const p = v => (v < 10 ? `0${v}` : String(v));
  return `${d.getUTCFullYear()}-${p(d.getUTCMonth() + 1)}-${p(d.getUTCDate())}`;
}

function isRealDate(ymd) {
  if (!YMD.test(ymd)) return false;
  return addDays(ymd, 0) === ymd;
}

const plural = (n, w) => `${n} ${w}${n === 1 ? '' : 's'}`;

let uid = 0;

export default {
  name: 'FollowupPopover',
  components: { FuIcon },
  props: {
    name: { type: String, default: '' },
    eyebrow: { type: String, default: '' },
    fu: { type: Object, required: true },
    attempts: { type: Number, default: 0 },
    statusLabel: { type: String, default: '' },
    today: { type: String, required: true },
    dateFormat: { type: String, default: 'MM/DD/YYYY' },
    anchor: { type: Object, default: null },             // the cell's DOMRect (plain object)
    layout: { type: String, default: 'desktop' },
    showAssign: { type: Boolean, default: false },
    employees: { type: Array, default: () => [] },       // [{ EmployeeID, Name }]
    initialEmployeeId: { type: String, default: '' },
    notePlaceholder: { type: String, default: 'Optional. Add notes here...' },
  },

  data() {
    uid += 1;
    return {
      dateId: `fu-pop-${uid}`,
      date: '',
      preset: null,
      note: '',
      employeeId: this.initialEmployeeId || '',
      confirming: null,          // null | 'danger' | 'restart'
      viewport: { w: window.innerWidth || 1280, h: window.innerHeight || 800 },
    };
  },

  computed: {
    isPhone() {
      return this.layout === 'phone' || this.viewport.w < 640;
    },
    isInitial() {
      return this.fu.state === 'notset';
    },
    canSchedule() {
      return this.fu.editable === true && ['notset', 'due', 'past', 'upcoming', 'nodate'].indexOf(this.fu.state) !== -1;
    },
    hasDanger() {
      return this.canSchedule && !this.isInitial && !!this.fu.dangerAction;
    },
    firstName() {
      const n = String(this.name || '');
      const first = (n.split(', ')[1] || n).trim().split(' ')[0];
      return first || 'This patient';
    },
    title() {
      return {
        notset: 'Start following up',
        nodate: 'Set a follow-up date',
        due: 'Due today',
        past: 'Past due',
        upcoming: 'Follow-up scheduled',
        stopped: 'Follow-ups stopped',
        done: 'Follow-ups done',
      }[this.fu.state] || 'Follow-up';
    },
    sub() {
      const fn = this.firstName;
      const n = this.fu.date ? daysBetween(this.today, this.fu.date) : null;
      const when = formatDate(this.fu.date, this.dateFormat);
      switch (this.fu.state) {
        case 'notset': return `Pick the day ${fn} should land on your call list.`;
        case 'nodate': return `${fn} has no follow-up date yet.`;
        case 'due': return `${fn} is on today's call list.`;
        case 'past': return n === null ? '' : `Was due ${when} — ${plural(-n, 'day')} ago.`;
        case 'upcoming': return n === null ? '' : `Scheduled for ${when} · in ${plural(n, 'day')}.`;
        case 'stopped': return `No more follow-ups are scheduled. ${plural(this.attempts, 'attempt')} made${this.statusLabel ? ` · ${this.statusLabel}` : ''}.`;
        case 'done': return `${fn} was contacted ${plural(this.attempts, 'time')} and the cycle has ended.`;
        default: return '';
      }
    },
    dateLabel() {
      if (this.isInitial) return 'First follow-up';
      if (this.fu.state === 'nodate') return 'Follow-up date';
      return 'Move follow-up to';
    },
    presets() {
      const list = this.isInitial
        ? [['Today', 0], ['Tomorrow', 1], ['In 3 days', 3], ['Next week', 7], ['In 2 weeks', 14]]
        : [['Tomorrow', 1], ['In 3 days', 3], ['Next week', 7], ['In 2 weeks', 14], ['In a month', 30]];
      return list.map(([label, n]) => ({ label, date: addDays(this.today, n) }));
    },
    dateError() {
      if (!this.date) return '';
      if (!isRealDate(this.date)) return 'Enter a valid date.';
      if (this.date < this.today) return 'Pick today or a later date.';
      return '';
    },
    employeeChanged() {
      return this.showAssign && this.employeeId !== (this.initialEmployeeId || '');
    },
    canSave() {
      if (!this.date || this.dateError) return false;
      if (this.isInitial) return true;
      return this.date !== this.fu.date || this.employeeChanged;
    },
    danger() {
      const fn = this.firstName;
      return this.fu.dangerAction === 'remove-initial'
        ? {
          title: 'Remove initial follow-up',
          desc: `Takes ${fn} off the schedule. Nothing has been attempted yet.`,
          cta: 'Remove',
          confirmText: `Remove the initial follow-up for ${fn}?`,
          confirmCta: 'Remove follow-up',
        }
        : {
          title: 'Stop follow-ups',
          desc: 'Ends the cycle — no more follow-ups get scheduled.',
          cta: 'Stop',
          confirmText: `Stop all future follow-ups for ${fn}?`,
          confirmCta: 'Stop follow-ups',
        };
    },
    panelStyle() {
      if (this.isPhone) return {};
      const vw = this.viewport.w;
      const vh = this.viewport.h;
      const a = this.anchor || { top: EDGE, bottom: EDGE, left: EDGE, right: EDGE + WIDTH };
      const left = Math.max(EDGE, Math.min(a.right - WIDTH, vw - WIDTH - EDGE));
      const below = vh - a.bottom;
      if (below < EST_HEIGHT + 16 && a.top > below) {
        return {
          left: `${left}px`,
          bottom: `${Math.max(EDGE, vh - a.top + 6)}px`,
          maxHeight: `${Math.max(160, a.top - 6 - EDGE)}px`,
        };
      }
      const top = Math.max(EDGE, a.bottom + 6);
      return {
        left: `${left}px`,
        top: `${top}px`,
        maxHeight: `${Math.max(160, vh - top - EDGE)}px`,
      };
    },
  },

  mounted() {
    this.readViewport();
    window.addEventListener('resize', this.onViewportChange);
    window.addEventListener('scroll', this.onScroll, true);
    document.addEventListener('keydown', this.onKey, true);
    this.$nextTick(() => {
      const panel = this.$refs.panel;
      const first = panel && panel.querySelector('button.fu-pop__preset, input, select, textarea, button');
      if (first && typeof first.focus === 'function') first.focus({ preventScroll: true });
    });
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.onViewportChange);
    window.removeEventListener('scroll', this.onScroll, true);
    document.removeEventListener('keydown', this.onKey, true);
  },

  methods: {
    readViewport() {
      this.viewport = { w: window.innerWidth || 1280, h: window.innerHeight || 800 };
    },
    onViewportChange() {
      // The cell moved under the popover; closing is clearer than floating detached.
      if (this.isPhone) this.readViewport();
      else this.$emit('close');
    },
    onScroll(e) {
      // Scrolling inside the popover (a tall phone sheet) must not close it.
      const panel = this.$refs.panel;
      if (panel && e && e.target && panel.contains(e.target)) return;
      if (!this.isPhone) this.$emit('close');
    },
    onKey(e) {
      if (e.key === 'Escape' || e.key === 'Esc') {
        e.stopPropagation();
        this.$emit('close');
      }
    },
    pickPreset(p) {
      this.preset = p.label;
      this.date = p.date;
    },
    onDateInput(e) {
      this.date = e.target.value;
      this.preset = null;
    },
    save() {
      if (!this.canSave) return;
      this.$emit('save', {
        kind: this.isInitial ? 'initial' : 'change',
        date: this.date,
        note: this.isInitial ? this.note.trim() : '',
        employeeId: this.employeeId,
      });
    },
    confirmDanger() {
      this.$emit(this.fu.dangerAction === 'remove-initial' ? 'remove-initial' : 'stop');
    },
    confirmRestart() {
      this.$emit('restart');
    },
  },
};
</script>

<style lang="scss" scoped>
@import './mixins';

.fu-pop__scrim {
  @include fu-scrim(false);
}

.fu-pop__scrim--tint {
  background: rgba(12, 15, 14, 0.32);
}

.fu-pop {
  @include fu-popover-surface;
  @include fu-font-base;
  position: fixed;
  z-index: $fu-z-popover;
  width: 344px;
  max-width: calc(100vw - 24px);
}

// Phone: a bottom sheet. ⛔ Its buttons sit LEFT (see the header) — the agent button covers the
// sheet's bottom-right corner, and it is z 9999, above anything a popover may use.
.fu-pop--phone {
  left: 8px;
  right: 8px;
  bottom: 8px;
  width: auto;
  max-width: none;
  max-height: calc(100% - 16px);
  border-radius: $fu-radius-lg;

  .fu-pop__actions { justify-content: flex-start; }
  .fu-pop__danger-row { flex-wrap: wrap; }
  .fu-pop__input { font-size: 16px; }
}

.fu-pop__head {
  padding: 14px $fu-space-4 $fu-space-3;
  border-bottom: 1px solid $fu-border;
}

.fu-pop__eyebrow {
  @include fu-eyebrow($fu-text-subtle, 10.5px);
  @include fu-ellipsis;
  letter-spacing: 0.08em;
}

.fu-pop__title {
  @include fu-font-display;
  font-weight: 800;
  font-size: 18px;
  letter-spacing: -0.02em;
  margin-top: 5px;
}

.fu-pop__sub {
  font-size: 12.5px;
  color: $fu-text-muted;
  margin-top: 2px;
  line-height: 1.45;
}

.fu-pop__section {
  padding: 14px $fu-space-4;
}

.fu-pop__section + .fu-pop__section {
  border-top: 1px solid $fu-border;
}

.fu-pop__label {
  display: block;
  margin: 0;
  font-size: 12.5px;
  font-weight: $fu-fw-bold;
  color: $fu-neutral-700;
}

.fu-pop__label--gap {
  margin: 14px 0 6px;
}

.fu-pop__optional {
  font-weight: $fu-fw-medium;
  color: $fu-text-subtle;
}

.fu-pop__presets {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin: $fu-space-2 0 10px;
}

.fu-pop__preset {
  @include fu-button-reset;
  padding: 5px 10px;
  border-radius: $fu-radius-sm + 2px;
  font-size: 12.5px;
  font-weight: $fu-fw-semibold;
  border: 1px solid $fu-border;
  background: $fu-surface;
  color: $fu-neutral-600;
  &:hover { background: $fu-neutral-50; }
  @include fu-focus-ring;
}

.fu-pop__preset--on {
  border-color: $fu-brand;
  background: $fu-brand-50;
  color: $fu-brand-700;
  &:hover { background: $fu-brand-50; }
}

.fu-pop__input {
  @include fu-input(40px);
}

.fu-pop__select {
  height: 42px;
  cursor: pointer;
}

.fu-pop__textarea {
  height: auto;
  padding: 10px 12px;
  resize: vertical;
  line-height: 1.45;
  font-size: 13.5px;
}

.fu-pop__error {
  margin-top: 6px;
  font-size: 12.5px;
  font-weight: $fu-fw-semibold;
  color: $fu-danger-text;
}

.fu-pop__actions {
  display: flex;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: $fu-space-2;
  margin-top: 14px;
}

.fu-pop__btn--primary { @include fu-btn(primary, sm); }
.fu-pop__btn--secondary { @include fu-btn(secondary, sm); }
.fu-pop__btn--ghost { @include fu-btn(ghost, sm); }
.fu-pop__btn--danger { @include fu-btn(danger, sm); }

.fu-pop__danger {
  padding: $fu-space-3 $fu-space-4 14px;
  border-top: 1px solid $fu-border;
  background: $fu-neutral-25;
}

.fu-pop__danger-row {
  display: flex;
  align-items: center;
  gap: $fu-space-3;
}

.fu-pop__danger-text {
  flex: 1;
  min-width: 0;
}

.fu-pop__danger-title {
  font-weight: $fu-fw-bold;
  font-size: 13px;
}

.fu-pop__danger-desc {
  font-size: 12px;
  color: $fu-text-muted;
  margin-top: 1px;
  line-height: 1.4;
}

.fu-pop__danger-cta {
  @include fu-button-reset;
  flex: none;
  padding: 6px 11px;
  border-radius: $fu-radius-sm + 2px;
  font-size: 12.5px;
  font-weight: $fu-fw-bold;
  color: $fu-danger;
  border: 1px solid $fu-danger-border;
  background: $fu-surface;
  &:hover { background: $fu-danger-subtle; }
  @include fu-focus-ring;
}

.fu-pop__confirm-text {
  font-weight: $fu-fw-bold;
  font-size: 13px;
  color: $fu-danger-text;
}

.fu-pop__confirm-text--plain {
  color: $fu-text;
  margin-top: 14px;
}

.fu-pop__info {
  display: flex;
  gap: 10px;
  padding: $fu-space-3;
  border-radius: 11px;
  background: $fu-neutral-50;
  border: 1px solid $fu-border;
  font-size: 12.5px;
  color: $fu-neutral-600;
  line-height: 1.5;
}

.fu-pop__info-icon {
  flex: none;
  margin-top: 1px;
  color: $fu-text-muted;
}
</style>
